import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { SystemActivityFeed } from '../components/SystemActivityFeed';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import * as systemHealthService from '../services/systemHealthService';
import { ResponsiveLine } from '@nivo/line';

/**
 * System Health Page
 *
 * Platform Admin dashboard for monitoring system health, errors, and metrics.
 * Displays:
 * - System health metrics (uptime, errors, logins, API calls)
 * - Error logs with severity levels
 * - Time series graphs for errors and user logins
 */

interface SystemMetrics {
  uptime: number;
  totalErrors: number;
  totalLogins: number;
  totalApiCalls: number;
  errorRate: number;
  avgResponseTime: number;
}

interface ErrorLog {
  id: string;
  timestamp: string;
  level: 'error' | 'warning' | 'critical';
  message: string;
  stack?: string;
  endpoint?: string;
  user_id?: string;
  ip_address?: string;
}

interface TimeSeriesData {
  timestamp: string;
  value: number;
}

export function SystemHealth(): React.ReactElement {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([]);
  const [errorTimeSeries, setErrorTimeSeries] = useState<TimeSeriesData[]>([]);
  const [loginTimeSeries, setLoginTimeSeries] = useState<TimeSeriesData[]>([]);
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h');

  // Redirect if not Platform Admin
  useEffect(() => {
    if (user && user.role !== 'platform_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    loadSystemHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, token]);

  const loadSystemHealth = async () => {
    if (!token) return;

    setLoading(true);
    try {
      // Fetch real system health data
      const healthData = await systemHealthService.getSystemHealth(timeRange, token);
      setMetrics(healthData.metrics);

      // Fetch error logs
      const errors = await systemHealthService.getErrorLogs(timeRange, token, undefined, 50);
      setErrorLogs(errors as any);

      // Fetch error time series
      const errorData = await systemHealthService.getErrorTimeSeries(timeRange, token);
      setErrorTimeSeries(errorData.map(d => ({ timestamp: d.timestamp, value: d.count || 0 })));

      // Fetch login time series
      const loginData = await systemHealthService.getLoginTimeSeries(timeRange, token);
      setLoginTimeSeries(loginData.map(d => ({ timestamp: d.timestamp, value: d.count || 0 })));

    } catch (error) {
      console.error('Error loading system health:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatUptime = (uptime: number): string => {
    return `${uptime.toFixed(2)}%`;
  };

  const getLevelColor = (level: string): string => {
    switch (level) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'error':
        return 'bg-orange-100 text-orange-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getLevelIcon = (level: string): React.ReactElement => {
    switch (level) {
      case 'critical':
        return (
          <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="h-5 w-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="h-5 w-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return <></>;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading system health...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 px-4 sm:px-6 lg:px-8 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">System Health</h1>
            <p className="mt-2 text-gray-600">Monitor system performance, errors, and metrics</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setTimeRange('24h')}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                timeRange === '24h'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              24 Hours
            </button>
            <button
              onClick={() => setTimeRange('7d')}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                timeRange === '7d'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              7 Days
            </button>
            <button
              onClick={() => setTimeRange('30d')}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                timeRange === '30d'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              30 Days
            </button>
          </div>
        </div>

        {/* Main Grid Layout: 2/3 (System Management) + 1/3 (Activity Feed) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: System Management (2/3 width) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Metrics Cards */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Uptime</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {metrics ? formatUptime(metrics.uptime) : '--'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Errors</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {metrics?.totalErrors || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">User Logins</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {metrics?.totalLogins || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">API Calls</dt>
                    <dd className="text-2xl font-semibold text-gray-900">
                      {metrics?.totalApiCalls.toLocaleString() || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
            </div>

            {/* Time Series Graphs */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Errors Over Time */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-5 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Errors Over Time</h3>
              <p className="mt-1 text-sm text-gray-500">Error count by hour</p>
            </div>
            <div className="px-6 py-5">
              <div className="h-64">
                {errorTimeSeries.length > 0 ? (
                  <ResponsiveLine
                    data={[
                      {
                        id: 'errors',
                        data: errorTimeSeries.slice(-24).map(d => ({
                          x: new Date(d.timestamp),
                          y: d.value
                        }))
                      }
                    ]}
                    margin={{ top: 10, right: 20, bottom: 50, left: 50 }}
                    xScale={{
                      type: 'time',
                      format: 'native',
                      precision: 'hour'
                    }}
                    xFormat="time:%H:%M"
                    yScale={{
                      type: 'linear',
                      min: 0,
                      max: 'auto'
                    }}
                    yFormat=" >-.0f"
                    axisBottom={{
                      format: '%H:%M',
                      tickRotation: -45,
                      legend: 'Time',
                      legendOffset: 40,
                      legendPosition: 'middle'
                    }}
                    axisLeft={{
                      legend: 'Errors',
                      legendOffset: -40,
                      legendPosition: 'middle',
                      format: value => Number.isInteger(value) ? value : ''
                    }}
                    colors={['#ef4444']}
                    lineWidth={3}
                    pointSize={8}
                    pointColor="#ef4444"
                    pointBorderWidth={2}
                    pointBorderColor="#ffffff"
                    enableArea={true}
                    areaOpacity={0.1}
                    useMesh={true}
                    curve="monotoneX"
                    enableGridX={false}
                    enableGridY={true}
                    gridYValues={5}
                    tooltip={({ point }) => (
                      <div style={{
                        background: 'white',
                        padding: '9px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <strong style={{ color: '#ef4444' }}>{point.data.yFormatted} errors</strong>
                        <br />
                        <span style={{ fontSize: '11px', color: '#6b7280' }}>
                          at {new Date(point.data.x as Date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                    theme={{
                      axis: {
                        ticks: {
                          text: {
                            fontSize: 11,
                            fill: '#6b7280'
                          }
                        },
                        legend: {
                          text: {
                            fontSize: 12,
                            fill: '#374151',
                            fontWeight: 500
                          }
                        }
                      },
                      grid: {
                        line: {
                          stroke: '#e5e7eb',
                          strokeWidth: 1
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    No data available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* User Logins Over Time */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-5 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">User Logins Over Time</h3>
              <p className="mt-1 text-sm text-gray-500">Login count by hour</p>
            </div>
            <div className="px-6 py-5">
              <div className="h-64">
                {loginTimeSeries.length > 0 ? (
                  <ResponsiveLine
                    data={[
                      {
                        id: 'logins',
                        data: loginTimeSeries.slice(-24).map(d => ({
                          x: new Date(d.timestamp),
                          y: d.value
                        }))
                      }
                    ]}
                    margin={{ top: 10, right: 20, bottom: 50, left: 50 }}
                    xScale={{
                      type: 'time',
                      format: 'native',
                      precision: 'hour'
                    }}
                    xFormat="time:%H:%M"
                    yScale={{
                      type: 'linear',
                      min: 0,
                      max: 'auto'
                    }}
                    yFormat=" >-.0f"
                    axisBottom={{
                      format: '%H:%M',
                      tickRotation: -45,
                      legend: 'Time',
                      legendOffset: 40,
                      legendPosition: 'middle'
                    }}
                    axisLeft={{
                      legend: 'Logins',
                      legendOffset: -40,
                      legendPosition: 'middle',
                      format: value => Number.isInteger(value) ? value : ''
                    }}
                    colors={['#3b82f6']}
                    lineWidth={3}
                    pointSize={8}
                    pointColor="#3b82f6"
                    pointBorderWidth={2}
                    pointBorderColor="#ffffff"
                    enableArea={true}
                    areaOpacity={0.1}
                    useMesh={true}
                    curve="monotoneX"
                    enableGridX={false}
                    enableGridY={true}
                    gridYValues={5}
                    tooltip={({ point }) => (
                      <div style={{
                        background: 'white',
                        padding: '9px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <strong style={{ color: '#3b82f6' }}>{point.data.yFormatted} logins</strong>
                        <br />
                        <span style={{ fontSize: '11px', color: '#6b7280' }}>
                          at {new Date(point.data.x as Date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                    theme={{
                      axis: {
                        ticks: {
                          text: {
                            fontSize: 11,
                            fill: '#6b7280'
                          }
                        },
                        legend: {
                          text: {
                            fontSize: 12,
                            fill: '#374151',
                            fontWeight: 500
                          }
                        }
                      },
                      grid: {
                        line: {
                          stroke: '#e5e7eb',
                          strokeWidth: 1
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    No data available
                  </div>
                )}
              </div>
            </div>
          </div>
            </div>

            {/* Error Logs */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-5 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Recent Errors</h3>
                <p className="mt-1 text-sm text-gray-500">Latest error logs from the system</p>
              </div>
              <div className="divide-y divide-gray-200">
            {errorLogs.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No errors</h3>
                <p className="mt-1 text-sm text-gray-500">System is running smoothly!</p>
              </div>
            ) : (
              errorLogs.map((error) => (
                <div key={error.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="flex-shrink-0 pt-0.5">
                        {getLevelIcon(error.level)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLevelColor(error.level)}`}>
                            {error.level.toUpperCase()}
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(error.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="mt-2 text-sm font-medium text-gray-900">{error.message}</p>
                        {error.endpoint && (
                          <p className="mt-1 text-sm text-gray-500">
                            Endpoint: <span className="font-mono">{error.endpoint}</span>
                          </p>
                        )}
                        {error.ip_address && (
                          <p className="mt-1 text-sm text-gray-500">
                            IP Address: <span className="font-mono">{error.ip_address}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
                )}
              </div>
              {errorLogs.length > 0 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <button className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                    View all logs →
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Activity Feed (1/3 width) */}
          <div className="lg:col-span-1">
            <SystemActivityFeed />
          </div>
        </div>
      </div>
    </Layout>
  );
}
